/**
 * Helper functions for user information from Okta tokens and API calls
 */
import {jwtDecode} from 'jwt-decode'
import {ENDPOINTS} from './constants/apiEndpoints'
import {get, put, post, del} from '@/services/apiClient'

interface UserProfile {
  gpId?: string
  name?: string
  email?: string
  nameInitial?: string
  firstName?: string
  lastName?: string
  userConfig?: string[]
}

export interface FavoriteItem {
  id: string
  name: string
  type: 'Team Dashboard' | 'Program Dashboard'
}

interface UserInfo {
  id?: string
  username: string
  theme?: 'light' | 'dark'
  favourites?: FavoriteItem[]
  seMetricsTableSettings?: Record<string, Record<string, string[]>>
}

interface ThemeRequest {
  username: string
  theme: 'light' | 'dark'
}

interface AddFavoriteRequest {
  userId: string
  favorite: FavoriteItem
  existingFavorites?: FavoriteItem[] // Optional array of existing favorites
}

interface DeleteFavoriteRequest {
  userId: string
  favoriteId: string
  favoriteType: string
  remainingFavorites?: FavoriteItem[] // Optional array of remaining favorites after deletion
}

/**
 * Get user profile information from Okta token storage
 * @returns UserProfile object or null if not available
 */
export const getUserProfileFromOkta = (): UserProfile | null => {
  try {
    const oktaStorage = JSON.parse(localStorage.getItem('okta-token-storage') ?? '{}')

    const idToken = oktaStorage?.accessToken

    if (!idToken?.accessToken) return null

    // Use jwt_decode package to decode the token
    const userProfile = jwtDecode<{
      FirstName?: string
      LastName?: string
      email?: string
      gpid?: string
      pepapppsdashboardpermission?: string[]
    }>(idToken.accessToken)

    // Generate name initials from first and last name
    const nameInitial = (userProfile?.FirstName?.[0] ?? '') + (userProfile?.LastName?.[0] ?? '')

    return {
      gpId: userProfile?.gpid,
      name: userProfile?.FirstName && userProfile?.LastName ? `${userProfile.FirstName} ${userProfile.LastName}` : '',
      email: userProfile?.email,
      nameInitial: nameInitial || '',
      firstName: userProfile?.FirstName,
      lastName: userProfile?.LastName,
      userConfig:
        userProfile?.pepapppsdashboardpermission && userProfile.pepapppsdashboardpermission.length > 0
          ? userProfile.pepapppsdashboardpermission
          : ['Regular User', 'ru'],
    }
  } catch (error) {
    console.error('Error retrieving user info from Okta token:', error)
    return null
  }
}

/**
 * Get just the user's initials from Okta token
 * @returns User's initial or 'U' as fallback
 */
export const getUserInitial = (): string => {
  const profile = getUserProfileFromOkta()
  return profile?.nameInitial ?? 'U'
}

/**
 * Fetch user information from the backend API
 * @param username - The username/email to fetch user info for
 * @returns Promise<UserInfo | null>
 */
export const fetchUserInfo = async (username: string): Promise<UserInfo | null> => {
  try {
    const response = await get(ENDPOINTS.GET_USER, {
      params: {username},
    })
    const userInfo = response.data
    // Default to 'light' theme if theme is null or missing
    if (userInfo && (!userInfo.theme || userInfo.theme === null)) {
      userInfo.theme = 'light'
    }
    // Ensure favourites is always an array - for main branch compatibility
    if (userInfo && (!userInfo.favourites || !Array.isArray(userInfo.favourites))) {
      userInfo.favourites = []
    }
    return userInfo
  } catch (error) {
    console.error('Error fetching user info:', error)
    return null
  }
}

/**
 * Update user theme via API
 * @param themeRequest - The theme update request
 * @returns Promise<UserInfo | null>
 */
export const updateUserTheme = async (themeRequest: ThemeRequest): Promise<UserInfo | null> => {
  try {
    const response = await put(ENDPOINTS.UPDATE_THEME, {
      data: themeRequest,
    })
    const userInfo = response.data
    // Default to 'light' theme if theme is null or missing in response
    if (userInfo && (!userInfo.theme || userInfo.theme === null)) {
      userInfo.theme = 'light'
    }
    return userInfo
  } catch (error) {
    console.error('Error updating user theme:', error)
    return null
  }
}

/**
 * Add a favorite for a user via API
 * @param request - The add favorite request with userId, favorite item, and all existing favorites
 * @returns Promise<UserInfo | null>
 */
export const addUserFavorite = async (request: AddFavoriteRequest): Promise<UserInfo | null> => {
  try {
    const endpoint = ENDPOINTS.ADD_FAVORITE.replace('{userId}', request.userId)

    // Create the payload with all favorites (existing + new)
    // The API expects the complete list of favorites, not just the new one
    // Null check for main branch compatibility
    const existingFavorites = Array.isArray(request.existingFavorites) ? request.existingFavorites : []
    const allFavorites = [...existingFavorites, request.favorite]

    // Format each favorite in the expected structure
    const payload = allFavorites.map(fav => ({
      type: fav.type,
      id: fav.id,
      name: fav.name,
    }))

    const response = await post(endpoint, {
      data: payload, // API expects an array with all favorites
    })
    return response.data
  } catch (error) {
    console.error('Error adding user favorite:', error)
    return null
  }
}

/**
 * Delete a favorite for a user via API
 * @param request - The delete favorite request with userId, favoriteId and favoriteType
 * @returns Promise<boolean>
 */
export const deleteUserFavorite = async (request: DeleteFavoriteRequest): Promise<boolean> => {
  try {
    const endpoint = ENDPOINTS.DELETE_FAVORITE.replace('{userId}', request.userId)

    // Create the payload for deletion
    const payload = {
      type: request.favoriteType,
      id: request.favoriteId,
    }

    await del(endpoint, {
      data: payload,
    })
    return true
  } catch (error) {
    console.error('Error deleting user favorite:', error)
    return false
  }
}

/**
 * Get current user's email from Okta token
 * @returns string | null
 */
export const getCurrentUserEmail = (): string | null => {
  const profile = getUserProfileFromOkta()
  return profile?.email ?? null
}
